import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3, ArrowLeft, Calendar, TrendingUp, TrendingDown, DollarSign, Users,
  Package, PieChart, Loader2, RefreshCw, Filter, Download, CreditCard, Building2, Banknote, Wallet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format, subMonths } from "date-fns";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, PieChart as RechartsPie, Pie, Cell
} from "recharts";

const CHART_COLORS = {
  primary: "hsl(195, 72%, 36%)",
  income: "hsl(158, 64%, 40%)",
  expense: "hsl(4, 74%, 55%)",
  accent1: "hsl(200, 90%, 42%)",
  accent2: "hsl(38, 92%, 50%)",
  accent3: "hsl(280, 60%, 50%)",
  muted: "hsl(210, 16%, 70%)"
};

const PIE_COLORS = [
  "#0ea5a9", "#3b82f6", "#8b5cf6", "#f59e0b", "#ef4444", 
  "#10b981", "#6366f1", "#ec4899", "#14b8a6", "#f97316"
];

export default function AnalyticsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [period, setPeriod] = useState("12"); // months or preset
  const [customDateRange, setCustomDateRange] = useState({ start: "", end: "" });
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data states
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [productsPerformance, setProductsPerformance] = useState([]);
  const [clientsMetrics, setClientsMetrics] = useState(null);
  const [salesBreakdown, setSalesBreakdown] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);

  // Calculate period months based on selection
  const getPeriodMonths = () => {
    const now = new Date();
    switch (period) {
      case "Q1": return 3; // Q1 (Jan-Mar)
      case "Q2": return 3; // Q2 (Apr-Jun)
      case "Q3": return 3; // Q3 (Jul-Sep)
      case "Q4": return 3; // Q4 (Oct-Dec)
      case "YTD": return now.getMonth() + 1; // Year to date
      case "1Y": return 12; // Last 1 year
      case "2Y": return 24; // Last 2 years
      case "custom": return 12; // Will use custom date range
      default: return parseInt(period) || 12;
    }
  };

  const fetchData = useCallback(async (showRefreshToast = false) => {
    if (showRefreshToast) setIsRefreshing(true);
    else setIsLoading(true);
    
    try {
      const months = getPeriodMonths();
      
      const [trendsRes, productsRes, clientsRes, breakdownRes, forecastRes, paymentMethodsRes, paymentSummaryRes] = await Promise.all([
        api.get(`/analytics/revenue-trends?months=${months}`),
        api.get(`/analytics/products-performance`),
        api.get(`/analytics/clients-metrics`),
        api.get(`/analytics/sales-breakdown`),
        api.get(`/analytics/forecast`),
        api.get(`/analytics/payment-methods`).catch(() => ({ data: null })),
        api.get(`/analytics/payment-methods/summary`).catch(() => ({ data: null }))
      ]);
      
      // Set payment analytics data
      setPaymentAnalytics(paymentMethodsRes.data);
      setPaymentSummary(paymentSummaryRes.data);
      
      // Transform revenue trends data for charts
      const trendsData = trendsRes.data?.periods?.map(p => ({
        ...p,
        month_label: p.period
      })) || [];
      setRevenueTrends(trendsData);
      
      // Transform products performance data
      const productsData = productsRes.data?.products?.map(p => ({
        ...p,
        average_price: p.average_sale_value
      })) || [];
      setProductsPerformance(productsData);
      
      // Transform clients metrics
      const clientsData = {
        total_clients: clientsRes.data?.summary?.total_clients || 0,
        active_clients: clientsRes.data?.summary?.active_clients || 0,
        new_clients: clientsRes.data?.acquisition_trend?.reduce((sum, m) => sum + m.new_clients, 0) || 0,
        avg_revenue_per_client: 0,
        top_clients: clientsRes.data?.top_clients?.map(c => ({
          ...c,
          purchases_count: c.sales_count
        })) || [],
        acquisition_trend: clientsRes.data?.acquisition_trend?.map(t => ({
          ...t,
          month_label: t.period
        })) || []
      };
      // Calculate avg revenue per client
      if (clientsData.total_clients > 0 && clientsData.top_clients.length > 0) {
        const totalRevenue = clientsData.top_clients.reduce((sum, c) => sum + (c.total_revenue || 0), 0);
        clientsData.avg_revenue_per_client = totalRevenue / clientsData.total_clients;
      }
      setClientsMetrics(clientsData);
      
      // Sales breakdown - convert by_status to have total_amount for pie chart
      const breakdownData = {
        ...breakdownRes.data,
        by_status: breakdownRes.data?.by_status?.map(s => ({
          ...s,
          total_amount: s.revenue
        })) || []
      };
      setSalesBreakdown(breakdownData);
      
      // Transform forecast data
      const forecastData = {
        forecast_data: [
          ...(forecastRes.data?.historical_monthly?.map((h, i) => {
            const date = new Date();
            date.setMonth(date.getMonth() - (5 - i));
            return {
              month_label: date.toLocaleDateString('en', { month: 'short', year: 'numeric' }),
              actual: h.revenue,
              forecast: null
            };
          }) || []),
          ...(forecastRes.data?.forecasts?.map(f => ({
            month_label: f.period,
            actual: null,
            forecast: f.forecast_revenue
          })) || [])
        ],
        summary: {
          next_month: forecastRes.data?.forecasts?.[0]?.forecast_revenue || 0,
          next_quarter: forecastRes.data?.forecasts?.reduce((sum, f) => sum + (f.forecast_revenue || 0), 0) || 0,
          confidence: forecastRes.data?.data_points >= 6 ? 75 : forecastRes.data?.data_points >= 3 ? 50 : 25
        }
      };
      setForecast(forecastData);
      
      if (showRefreshToast) {
        toast.success(t("data_refreshed", "common") || "Data refreshed");
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      if (error.response?.status === 403) {
        toast.error("This feature is only available for business accounts");
        navigate("/dashboard");
      } else {
        toast.error(t("failed_load_data", "dashboard") || "Failed to load data");
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [period, navigate, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3">
        <p className="font-medium text-sm mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' && entry.value > 100 
              ? formatCurrency(entry.value) 
              : entry.value}
          </p>
        ))}
      </div>
    );
  };

  // Calculate summary metrics
  const summaryMetrics = {
    totalRevenue: revenueTrends.reduce((sum, m) => sum + (m.revenue || 0), 0),
    totalSales: revenueTrends.reduce((sum, m) => sum + (m.sales_count || 0), 0),
    avgMonthlyRevenue: revenueTrends.length > 0 
      ? revenueTrends.reduce((sum, m) => sum + (m.revenue || 0), 0) / revenueTrends.length 
      : 0,
    growthRate: revenueTrends.length >= 2 
      ? ((revenueTrends[revenueTrends.length - 1]?.revenue || 0) - (revenueTrends[0]?.revenue || 0)) / 
        Math.max(revenueTrends[0]?.revenue || 1, 1) * 100
      : 0
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="max-w-[1400px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-[300px] w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("analytics", "company") || "Sales Analytics"}
              </h1>
              <p className="text-xs text-muted-foreground">
                {t("analytics_subtitle", "company") || "Performance insights and trends"}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[180px]" data-testid="period-select">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Preset Periods</SelectLabel>
                  <SelectItem value="3">Last 3 months</SelectItem>
                  <SelectItem value="6">Last 6 months</SelectItem>
                  <SelectItem value="12">Last 12 months</SelectItem>
                  <SelectItem value="24">Last 24 months</SelectItem>
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Quarters</SelectLabel>
                  <SelectItem value="Q1">Q1 (Jan-Mar)</SelectItem>
                  <SelectItem value="Q2">Q2 (Apr-Jun)</SelectItem>
                  <SelectItem value="Q3">Q3 (Jul-Sep)</SelectItem>
                  <SelectItem value="Q4">Q4 (Oct-Dec)</SelectItem>
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Years</SelectLabel>
                  <SelectItem value="YTD">Year to Date</SelectItem>
                  <SelectItem value="1Y">Last 1 Year</SelectItem>
                  <SelectItem value="2Y">Last 2 Years</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => fetchData(true)} 
              disabled={isRefreshing}
              data-testid="refresh-button"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* Summary KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="kpi-total-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{t("total_revenue", "company") || "Total Revenue"}</p>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{formatCurrency(summaryMetrics.totalRevenue)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("last_n_months", "company")?.replace("{n}", period) || `Last ${period} months`}
              </p>
            </CardContent>
          </Card>
          
          <Card data-testid="kpi-total-sales">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{t("total_sales", "company") || "Total Sales"}</p>
                <Package className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{summaryMetrics.totalSales}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("transactions", "common") || "transactions"}
              </p>
            </CardContent>
          </Card>
          
          <Card data-testid="kpi-avg-monthly">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{t("avg_monthly", "company") || "Avg Monthly"}</p>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-2">{formatCurrency(summaryMetrics.avgMonthlyRevenue)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("revenue", "company") || "revenue"}
              </p>
            </CardContent>
          </Card>
          
          <Card data-testid="kpi-growth">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{t("growth", "company") || "Growth"}</p>
                {summaryMetrics.growthRate >= 0 
                  ? <TrendingUp className="h-4 w-4 text-green-600" />
                  : <TrendingDown className="h-4 w-4 text-red-600" />
                }
              </div>
              <p className={`text-2xl font-bold mt-2 ${summaryMetrics.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {summaryMetrics.growthRate >= 0 ? '+' : ''}{formatPercent(summaryMetrics.growthRate)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("period_over_period", "company") || "period over period"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different analytics views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-5 w-full max-w-2xl" data-testid="analytics-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">
              {t("overview", "common") || "Overview"}
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">
              {t("products", "company") || "Products"}
            </TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients">
              {t("clients", "company") || "Clients"}
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">
              Payments
            </TabsTrigger>
            <TabsTrigger value="forecast" data-testid="tab-forecast">
              {t("forecast", "company") || "Forecast"}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Trends Chart */}
              <Card className="lg:col-span-2" data-testid="revenue-trends-chart">
                <CardHeader>
                  <CardTitle className="text-lg">{t("revenue_trends", "company") || "Revenue Trends"}</CardTitle>
                  <CardDescription>{t("revenue_trends_desc", "company") || "Monthly revenue and sales count"}</CardDescription>
                </CardHeader>
                <CardContent>
                  {revenueTrends.length === 0 ? (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      {t("no_data_available", "common") || "No data available"}
                    </div>
                  ) : (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueTrends} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                          <defs>
                            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0.02}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ff-border))" />
                          <XAxis 
                            dataKey="month_label" 
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                            axisLine={{ stroke: 'hsl(var(--ff-border))' }}
                          />
                          <YAxis 
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                            axisLine={{ stroke: 'hsl(var(--ff-border))' }}
                            tickFormatter={(value) => `$${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Area 
                            type="monotone" 
                            dataKey="revenue" 
                            name={t("revenue", "company") || "Revenue"}
                            stroke={CHART_COLORS.primary} 
                            fill="url(#revenueGradient)"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sales Count Bar Chart */}
              <Card data-testid="sales-count-chart">
                <CardHeader>
                  <CardTitle className="text-lg">{t("sales_volume", "company") || "Sales Volume"}</CardTitle>
                  <CardDescription>{t("monthly_sales_count", "company") || "Number of sales per month"}</CardDescription>
                </CardHeader>
                <CardContent>
                  {revenueTrends.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      {t("no_data_available", "common") || "No data available"}
                    </div>
                  ) : (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={revenueTrends} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ff-border))" />
                          <XAxis 
                            dataKey="month_label" 
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          />
                          <YAxis 
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar 
                            dataKey="sales_count" 
                            name={t("sales", "company") || "Sales"}
                            fill={CHART_COLORS.income}
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sales Breakdown Pie Chart */}
              <Card data-testid="sales-breakdown-chart">
                <CardHeader>
                  <CardTitle className="text-lg">{t("sales_by_status", "company") || "Sales by Status"}</CardTitle>
                  <CardDescription>{t("distribution_by_status", "company") || "Distribution by payment status"}</CardDescription>
                </CardHeader>
                <CardContent>
                  {!salesBreakdown?.by_status?.length ? (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      {t("no_data_available", "common") || "No data available"}
                    </div>
                  ) : (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={salesBreakdown.by_status}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="total_amount"
                            nameKey="status"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {salesBreakdown.by_status.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => formatCurrency(value)}
                          />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Products by Revenue */}
              <Card className="lg:col-span-2" data-testid="products-revenue-chart">
                <CardHeader>
                  <CardTitle className="text-lg">{t("product_performance", "company") || "Product Performance"}</CardTitle>
                  <CardDescription>{t("revenue_by_product", "company") || "Revenue generated by each product/service"}</CardDescription>
                </CardHeader>
                <CardContent>
                  {productsPerformance.length === 0 ? (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      {t("no_products_data", "company") || "No product data available"}
                    </div>
                  ) : (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={productsPerformance.slice(0, 10)} 
                          layout="vertical"
                          margin={{ left: 100, right: 20, top: 10, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ff-border))" />
                          <XAxis 
                            type="number"
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                            tickFormatter={(value) => `$${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                          />
                          <YAxis 
                            type="category"
                            dataKey="name" 
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                            width={90}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar 
                            dataKey="total_revenue" 
                            name={t("revenue", "company") || "Revenue"}
                            fill={CHART_COLORS.accent1}
                            radius={[0, 4, 4, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Products Stats Cards */}
              {productsPerformance.slice(0, 4).map((product, index) => (
                <Card key={product.id || index} data-testid={`product-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <Badge variant="outline" className="mt-1">
                          {product.type === "product" ? (t("product", "company") || "Product") : (t("service", "company") || "Service")}
                        </Badge>
                      </div>
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">{t("revenue", "company") || "Revenue"}</span>
                        <span className="font-medium">{formatCurrency(product.total_revenue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">{t("sales_count", "company") || "Sales"}</span>
                        <span className="font-medium">{product.sales_count || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">{t("avg_price", "company") || "Avg Price"}</span>
                        <span className="font-medium">{formatCurrency(product.average_price)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card data-testid="clients-total">
                <CardContent className="p-6 text-center">
                  <Users className="h-8 w-8 mx-auto text-[hsl(var(--ff-primary))] mb-2" />
                  <p className="text-3xl font-bold">{clientsMetrics?.total_clients || 0}</p>
                  <p className="text-sm text-muted-foreground">{t("total_clients", "company") || "Total Clients"}</p>
                </CardContent>
              </Card>
              <Card data-testid="clients-new">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="h-8 w-8 mx-auto text-green-600 mb-2" />
                  <p className="text-3xl font-bold text-green-600">{clientsMetrics?.new_clients || 0}</p>
                  <p className="text-sm text-muted-foreground">{t("new_clients", "company") || "New Clients"}</p>
                </CardContent>
              </Card>
              <Card data-testid="clients-avg-revenue">
                <CardContent className="p-6 text-center">
                  <DollarSign className="h-8 w-8 mx-auto text-[hsl(var(--ff-primary))] mb-2" />
                  <p className="text-3xl font-bold">{formatCurrency(clientsMetrics?.avg_revenue_per_client || 0)}</p>
                  <p className="text-sm text-muted-foreground">{t("avg_per_client", "company") || "Avg per Client"}</p>
                </CardContent>
              </Card>
            </div>

            {/* Top Clients List */}
            <Card data-testid="top-clients">
              <CardHeader>
                <CardTitle className="text-lg">{t("top_clients", "company") || "Top Clients"}</CardTitle>
                <CardDescription>{t("by_revenue", "company") || "Ranked by total revenue"}</CardDescription>
              </CardHeader>
              <CardContent>
                {!clientsMetrics?.top_clients?.length ? (
                  <div className="py-8 text-center text-muted-foreground">
                    {t("no_client_data", "company") || "No client data available"}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clientsMetrics.top_clients.slice(0, 5).map((client, index) => (
                      <div key={client.id || index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[hsl(var(--ff-primary)/.1)] flex items-center justify-center text-sm font-medium text-[hsl(var(--ff-primary))]">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{client.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {client.purchases_count || 0} {t("purchases", "company") || "purchases"}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatCurrency(client.total_revenue)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Acquisition Chart */}
            <Card data-testid="client-acquisition-chart">
              <CardHeader>
                <CardTitle className="text-lg">{t("client_acquisition", "company") || "Client Acquisition"}</CardTitle>
                <CardDescription>{t("new_clients_over_time", "company") || "New clients over time"}</CardDescription>
              </CardHeader>
              <CardContent>
                {!clientsMetrics?.acquisition_trend?.length ? (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    {t("no_data_available", "common") || "No data available"}
                  </div>
                ) : (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={clientsMetrics.acquisition_trend} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ff-border))" />
                        <XAxis 
                          dataKey="month_label" 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line 
                          type="monotone" 
                          dataKey="new_clients" 
                          name={t("new_clients", "company") || "New Clients"}
                          stroke={CHART_COLORS.income}
                          strokeWidth={2}
                          dot={{ fill: CHART_COLORS.income, strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6">
            {/* Payment Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card data-testid="payment-kpi-total">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Total Received</p>
                    <DollarSign className="h-4 w-4 text-emerald-500" />
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {formatCurrency(paymentSummary?.total_received || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">All time</p>
                </CardContent>
              </Card>

              <Card data-testid="payment-kpi-this-month">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">This Month</p>
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {formatCurrency(paymentSummary?.this_month_received || 0)}
                  </p>
                  {paymentSummary?.month_over_month_change !== undefined && (
                    <p className={`text-xs mt-1 flex items-center gap-1 ${
                      paymentSummary.month_over_month_change >= 0 ? 'text-emerald-600' : 'text-red-500'
                    }`}>
                      {paymentSummary.month_over_month_change >= 0 ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {Math.abs(paymentSummary.month_over_month_change).toFixed(1)}% vs last month
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="payment-kpi-transactions">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Transactions</p>
                    <CreditCard className="h-4 w-4 text-purple-500" />
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {paymentSummary?.total_transactions || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {paymentSummary?.this_month_transactions || 0} this month
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="payment-kpi-methods">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Active Methods</p>
                    <Wallet className="h-4 w-4 text-amber-500" />
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {paymentSummary?.active_methods_count || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Payment channels</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Payment Distribution Pie Chart */}
              <Card data-testid="payment-distribution-chart">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <PieChart className="h-4 w-4" />
                    Payment Distribution
                  </CardTitle>
                  <CardDescription>Revenue breakdown by payment method</CardDescription>
                </CardHeader>
                <CardContent>
                  {paymentAnalytics?.payment_methods?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPie>
                        <Pie
                          data={paymentAnalytics.payment_methods}
                          dataKey="total_received"
                          nameKey="payment_method_name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ payment_method_name, percentage_of_total }) => 
                            `${payment_method_name}: ${percentage_of_total}%`
                          }
                        >
                          {paymentAnalytics.payment_methods.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                      </RechartsPie>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      <div className="text-center">
                        <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No payment data yet</p>
                        <p className="text-xs mt-1">Record sales with payments to see analytics</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payment Methods Performance Bar Chart */}
              <Card data-testid="payment-performance-chart">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Payment Methods Performance
                  </CardTitle>
                  <CardDescription>Revenue by payment method</CardDescription>
                </CardHeader>
                <CardContent>
                  {paymentAnalytics?.payment_methods?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={paymentAnalytics.payment_methods} 
                        layout="vertical"
                        margin={{ left: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          type="number" 
                          tickFormatter={(value) => formatCurrency(value)}
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          type="category" 
                          dataKey="payment_method_name" 
                          stroke="hsl(var(--muted-foreground))"
                          width={80}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar 
                          dataKey="total_received" 
                          name="Total Received"
                          fill={CHART_COLORS.primary} 
                          radius={[0, 4, 4, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      <div className="text-center">
                        <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No payment data yet</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Payment Methods by Type */}
            {paymentSummary?.by_type && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(paymentSummary.by_type).map(([type, data]) => {
                  if (data.total === 0 && data.methods?.length === 0) return null;
                  
                  const typeConfig = {
                    gateway: { icon: CreditCard, color: "text-purple-500", bg: "bg-purple-50 dark:bg-purple-900/20", label: "Payment Gateways" },
                    bank: { icon: Building2, color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-900/20", label: "Bank Accounts" },
                    cash: { icon: Banknote, color: "text-green-500", bg: "bg-green-50 dark:bg-green-900/20", label: "Cash" },
                    check: { icon: Package, color: "text-slate-500", bg: "bg-slate-50 dark:bg-slate-900/20", label: "Check" },
                    other: { icon: Wallet, color: "text-amber-500", bg: "bg-amber-50 dark:bg-amber-900/20", label: "Other" }
                  }[type] || { icon: Wallet, color: "text-gray-500", bg: "bg-gray-50", label: type };
                  
                  const Icon = typeConfig.icon;
                  
                  return (
                    <Card key={type} data-testid={`payment-type-${type}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`p-2 rounded-lg ${typeConfig.bg}`}>
                            <Icon className={`h-5 w-5 ${typeConfig.color}`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{typeConfig.label}</p>
                            <p className="text-xs text-muted-foreground">{data.count} transactions</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">Total</span>
                            <span className="font-semibold">{formatCurrency(data.total)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">This Month</span>
                            <span className="text-sm">{formatCurrency(data.this_month)}</span>
                          </div>
                          {data.methods?.length > 0 && (
                            <div className="pt-2 border-t mt-2">
                              <p className="text-xs text-muted-foreground mb-1">Methods:</p>
                              {data.methods.slice(0, 3).map((m, i) => (
                                <p key={i} className="text-xs truncate">{m.name}: {formatCurrency(m.total)}</p>
                              ))}
                              {data.methods.length > 3 && (
                                <p className="text-xs text-muted-foreground">+{data.methods.length - 3} more</p>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Top Payment Methods Table */}
            {paymentSummary?.top_methods?.length > 0 && (
              <Card data-testid="top-payment-methods">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Top Payment Methods
                  </CardTitle>
                  <CardDescription>Highest revenue generating payment methods</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {paymentSummary.top_methods.map((method, index) => {
                      const typeConfig = {
                        gateway: { icon: CreditCard, color: "text-purple-500" },
                        bank: { icon: Building2, color: "text-blue-500" },
                        cash: { icon: Banknote, color: "text-green-500" },
                        check: { icon: Package, color: "text-slate-500" },
                        other: { icon: Wallet, color: "text-amber-500" }
                      }[method.type] || { icon: Wallet, color: "text-gray-500" };
                      
                      const Icon = typeConfig.icon;
                      
                      return (
                        <div 
                          key={method.id} 
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted font-semibold text-sm">
                              {index + 1}
                            </div>
                            <Icon className={`h-4 w-4 ${typeConfig.color}`} />
                            <div>
                              <p className="font-medium">{method.name}</p>
                              <p className="text-xs text-muted-foreground capitalize">{method.type}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(method.total_received)}</p>
                            <p className="text-xs text-muted-foreground">{method.percentage}% of total</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {(!paymentAnalytics?.payment_methods?.length && !paymentSummary?.top_methods?.length) && (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <CreditCard className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                    <h3 className="font-semibold text-lg mb-2">No Payment Analytics Yet</h3>
                    <p className="text-muted-foreground text-sm max-w-md mx-auto">
                      Start recording sales with payments to see detailed analytics about your payment methods and revenue channels.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Forecast Tab */}
          <TabsContent value="forecast" className="space-y-6">
            <Card data-testid="forecast-chart">
              <CardHeader>
                <CardTitle className="text-lg">{t("revenue_forecast", "company") || "Revenue Forecast"}</CardTitle>
                <CardDescription>
                  {t("forecast_desc", "company") || "Projected revenue based on historical trends"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!forecast?.forecast_data?.length ? (
                  <div className="h-[350px] flex flex-col items-center justify-center text-muted-foreground">
                    <BarChart3 className="h-12 w-12 mb-4 opacity-50" />
                    <p>{t("insufficient_data", "company") || "Insufficient data for forecasting"}</p>
                    <p className="text-sm mt-2">{t("need_more_sales", "company") || "Record more sales to enable forecasting"}</p>
                  </div>
                ) : (
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={forecast.forecast_data} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0.02}/>
                          </linearGradient>
                          <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={CHART_COLORS.accent2} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={CHART_COLORS.accent2} stopOpacity={0.02}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ff-border))" />
                        <XAxis 
                          dataKey="month_label" 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          tickFormatter={(value) => `$${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="actual" 
                          name={t("actual", "company") || "Actual"}
                          stroke={CHART_COLORS.primary} 
                          fill="url(#actualGradient)"
                          strokeWidth={2}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="forecast" 
                          name={t("forecast", "company") || "Forecast"}
                          stroke={CHART_COLORS.accent2} 
                          fill="url(#forecastGradient)"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Forecast Summary */}
            {forecast?.summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card data-testid="forecast-next-month">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm text-muted-foreground mb-2">{t("next_month_forecast", "company") || "Next Month"}</p>
                    <p className="text-3xl font-bold text-[hsl(var(--ff-primary))]">
                      {formatCurrency(forecast.summary.next_month)}
                    </p>
                  </CardContent>
                </Card>
                <Card data-testid="forecast-next-quarter">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm text-muted-foreground mb-2">{t("next_quarter_forecast", "company") || "Next Quarter"}</p>
                    <p className="text-3xl font-bold text-[hsl(var(--ff-primary))]">
                      {formatCurrency(forecast.summary.next_quarter)}
                    </p>
                  </CardContent>
                </Card>
                <Card data-testid="forecast-confidence">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm text-muted-foreground mb-2">{t("confidence_level", "company") || "Confidence"}</p>
                    <p className="text-3xl font-bold">
                      {formatPercent(forecast.summary.confidence || 0)}
                    </p>
                    <Badge variant="outline" className="mt-2">
                      {(forecast.summary.confidence || 0) >= 70 
                        ? (t("high", "company") || "High") 
                        : (forecast.summary.confidence || 0) >= 40 
                          ? (t("medium", "company") || "Medium") 
                          : (t("low", "company") || "Low")}
                    </Badge>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
